import PropTypes from 'prop-types';
import * as Yup from 'yup';
import merge from 'lodash/merge';
import { isBefore } from 'date-fns';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Box, Stack, Button, Tooltip, TextField, IconButton, DialogActions, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
// components
import { Iconify } from '../../../components';
import { ColorSinglePicker } from '../../../components/color-utils';
import { FormProvider, RHFTextField, RHFSwitch } from '../../../components/hook-form';
// constants
import { COLOR_OPTIONS } from '../../../constants/CalenderConst';
// redux
import { useDispatch } from '../../../redux/store';
import { createEvent, updateEvent, deleteEvent, getEvents } from '../../../redux/slices/calendar';
import { updateError } from '../../../redux/slices/user';

const getInitialValues = (event, range) => {
    const _event = {
        title: '',
        description: '',
        textColor: '#1890FF',
        allDay: false,
        start: range ? new Date(range.start) : new Date(),
        end: range ? new Date(range.end) : new Date(),
    };

    if (event || range) {
        return merge({}, _event, event);
    }

    return _event;
};

export default function CalendarForm({ event, range, onCancel }) {

    const dispatch = useDispatch();

    const isCreating = Object.keys(event).length === 0;

    const EventSchema = Yup.object().shape({
        title: Yup.string().required('This field is required').max(255),
        description: Yup.string().max(5000),
    });

    const methods = useForm({
        resolver: yupResolver(EventSchema),
        defaultValues: getInitialValues(event, range),
    });

    const {
        reset,
        watch,
        control,
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const onSubmit = async (data) => {
        try {
            const newEvent = {
                title: data.title,
                description: data.description,
                textColor: data.textColor,
                allDay: data.allDay,
                start: data.start,
                end: data.end,
            };
            if (event.id) {
                await dispatch(updateEvent(event.id, newEvent));
            } else {
                await dispatch(createEvent(newEvent));
            }
            // Ensure the latest events are fetched from backend and reflected in the calendar
            await dispatch(getEvents());
            // Success toast via global notifier
            dispatch(updateError({ message: 'Event saved successfully', success: true }));
            onCancel();
            reset();
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };

    const handleDelete = async () => {
        if (!event.id) return;
        try {
            onCancel();
            await dispatch(deleteEvent(event.id));
            await dispatch(getEvents());
            dispatch(updateError({ message: 'Event deleted successfully', success: true }));
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };

    const values = watch();

    const isDateError = isBefore(new Date(values.end), new Date(values.start));

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2.5} sx={{ p: 3 }}>
                <RHFTextField
                    name="title"
                    label="Title"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Iconify icon="mdi:calendar-text-outline" width={18} height={18} />
                            </InputAdornment>
                        ),
                    }}
                />

                <RHFTextField
                    name="description"
                    label="Description"
                    multiline
                    rows={4}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Iconify icon="mdi:text-short" width={18} height={18} />
                            </InputAdornment>
                        ),
                    }}
                />

                <RHFSwitch name="allDay" label="All day" />

                <Controller
                    name="start"
                    control={control}
                    render={({ field }) => (
                        <MobileDateTimePicker
                            {...field}
                            label="Start date"
                            inputFormat={'dd/MM/yyyy HH:mm'}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Iconify icon="mdi:clock-outline" width={18} height={18} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />
                    )}
                />

                <Controller
                    name="end"
                    control={control}
                    render={({ field }) => (
                        <MobileDateTimePicker
                            {...field}
                            label="End date"
                            inputFormat={'dd/MM/yyyy HH:mm'}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    error={!!isDateError}
                                    helperText={isDateError && 'End date must be later than start date'}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Iconify icon="mdi:clock-outline" width={18} height={18} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />
                    )}
                />

                <Controller
                    name="textColor"
                    control={control}
                    render={({ field }) => (
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <Iconify icon="mdi:palette-outline" width={18} height={18} />
                                <Box component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>Event Color</Box>
                            </Stack>
                            <ColorSinglePicker value={field.value} onChange={field.onChange} colors={COLOR_OPTIONS} />
                        </Box>
                    )}
                />
            </Stack>

            <DialogActions>
                {!isCreating && (
                    <Tooltip title="Delete Event">
                        <IconButton onClick={handleDelete}>
                            <Iconify icon="solar:trash-bin-trash-bold" width={20} height={20} />
                        </IconButton>
                    </Tooltip>
                )}
                <Box sx={{ flexGrow: 1 }} />

                <Button variant="outlined" color="inherit" onClick={onCancel} startIcon={<Iconify icon="mdi:close" width={18} height={18} />}>
                    Cancel
                </Button>

                <LoadingButton type="submit" variant="contained" loading={isSubmitting} startIcon={<Iconify icon="mdi:content-save" width={18} height={18} />}>
                    Save
                </LoadingButton>
            </DialogActions>
        </FormProvider>
    );
}

CalendarForm.propTypes = {
    event: PropTypes.object,
    range: PropTypes.object,
    onCancel: PropTypes.func,
};
